import { createContext, useCallback, useContext, useEffect, useState, ReactNode } from "react";
import { api } from "./api";
import type { User } from "@/types";

interface AuthState {
  user: User | null;
  hasProfile: boolean;
  loading: boolean;
  loginWithGoogle: (idToken: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshProfileStatus: () => void;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [hasProfile, setHasProfile] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.auth
      .me()
      .then((res) => {
        setUser(res.user);
        setHasProfile(res.hasProfile);
      })
      .catch(() => {
        setUser(null);
      })
      .finally(() => setLoading(false));
  }, []);

  const loginWithGoogle = useCallback(async (idToken: string) => {
    const res = await api.auth.loginWithGoogle(idToken);
    setUser(res.user);
    setHasProfile(res.hasProfile);
  }, []);

  const logout = useCallback(async () => {
    await api.auth.logout();
    setUser(null);
    setHasProfile(false);
  }, []);

  const refreshProfileStatus = useCallback(() => {
    setHasProfile(true);
  }, []);

  return (
    <AuthContext.Provider
      value={{ user, hasProfile, loading, loginWithGoogle, logout, refreshProfileStatus }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
